import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { PositionMediaTypeEnum } from '../../enums';
import { PdfSettingsAlignEnum } from '../../../pdf-settings/enums';
import { FormInputChangedModel } from '../../models';
import { getImageSize } from '../../helpers';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { TextEditorComponent } from '../../components';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';

@Component({
  selector: 'rnpl-position-media-form',
  templateUrl: './position-media-form.component.html',
  styleUrls: ['./position-media-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PositionMediaFormComponent implements OnChanges, OnDestroy {

  private originalWidth = 0;
  private originalHeight = 0;
  private aspectRatio = 1;

  public readonly maxFileSizeMB = 10;
  public minImageSize: number = 24;
  public maxImageHeight: number = 432;
  public maxImageWidth: number = 864;
  public alignEnum = PdfSettingsAlignEnum;
  public positionMediaTypeEnum = PositionMediaTypeEnum;
  public images: FileList;

  // public textControl: FormControl = new FormControl();
  public alignmentControl: FormControl = new FormControl(PdfSettingsAlignEnum.LEFT);
  public heightControl: FormControl = new FormControl(this.minImageSize);
  public widthControl: FormControl = new FormControl(this.minImageSize);
  public useAspectRatioControl: FormControl = new FormControl(true);

  @Input() public imgUrl: string;
  @Input() public hideTypeControl = false;
  @Input() public readonly: boolean = false;
  @Input() public mediaType: PositionMediaTypeEnum;
  @Input() public textValue: string;
  @Input() public alignmentValue: PdfSettingsAlignEnum;
  @Input() public useAspectRatioValue: boolean;
  @Input() public heightValue: number;
  @Input() public widthValue: number;

  @Output() fieldChanged: EventEmitter<FormInputChangedModel> = new EventEmitter<FormInputChangedModel>();
  @Output() deleteFile: EventEmitter<null> = new EventEmitter<null>();
  @Output() updateFile: EventEmitter<{ file: File; width: number; height: number }> =
    new EventEmitter<{ file: File; width: number; height: number }>();

  @ViewChild('UploadFileInput', {static: false}) uploadFileInput: ElementRef<HTMLInputElement>;
  @ViewChild('textEditorComponent', {static: false}) textEditorComponent: TextEditorComponent;
  @ViewChild('imgRef', {static: false}) imgRef: ElementRef;

  private sizeChangeBuffer$: Subject<FormInputChangedModel> = new Subject<FormInputChangedModel>();
  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  constructor(
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
  ) {
    this.sizeChangeBuffer$
      .pipe(
        distinctUntilChanged(),
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((field: FormInputChangedModel) => {
        this.fieldChanged.emit(field);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      // if (changes.hasOwnProperty('textValue')) {
      //   this.textControl.patchValue(this.textValue);
      // }
      if (changes.hasOwnProperty('alignmentValue') && this.alignmentValue) {
        this.alignmentControl.patchValue(this.alignmentValue);
      }
      if (changes.hasOwnProperty('useAspectRatioValue') && this.useAspectRatioValue) {
        this.useAspectRatioControl.patchValue(this.useAspectRatioValue);
      }
      if (changes.hasOwnProperty('heightValue') && this.heightValue) {
        this.heightControl.patchValue(this.heightValue);
      }
      if (changes.hasOwnProperty('widthValue') && this.widthValue) {
        this.widthControl.patchValue(this.widthValue);
      }
      if (changes.hasOwnProperty('readonly') && this.readonly) {
        // this.mediaTypeControl.disable();
        // this.textControl.disable();
        this.alignmentControl.disable();
        this.heightControl.disable();
        this.widthControl.disable();
        this.useAspectRatioControl.disable();
      }
    }
  }

  updateSizeField(fieldValue: any, fieldName: 'width'|'height'): void {
    if (this.useAspectRatioControlValue) {

      const size = this.resizeWithAspect(fieldValue, fieldName);

      this.widthControl.patchValue(size.width);
      this.heightControl.patchValue(size.height);

      this.updateField(
        {
          width: size.width,
          height: size.height,
          useAspectRatio: this.useAspectRatioControl.value,
        },
        'dimensions'
      );
    } else {
      if (fieldName === 'height') {
        if (fieldValue < this.minImageSize) { fieldValue = this.minImageSize; }
        if (fieldValue > this.maxImageHeight) { fieldValue = this.maxImageHeight; }
        this.heightControl.patchValue(fieldValue);
      }

      if (fieldName === 'width') {
        if (fieldValue < this.minImageSize) { fieldValue = this.minImageSize; }
        if (fieldValue > this.maxImageWidth) { fieldValue = this.maxImageWidth; }
        this.widthControl.patchValue(fieldValue);
      }

      this.updateField(fieldValue, fieldName);
    }
  }

  public updateField(fieldValue: any, fieldName: string): void {
    this.sizeChangeBuffer$.next({fieldName, fieldValue})
    // this.fieldChanged.emit();
  }

  public deleteFiles(): void {
    if (!this.hideTypeControl) {
      this.resetImg();
    }

    this.resetImgInput();
    this.deleteFile.emit();
  }

  public resetImg(): void {
    this.images = null;
    this.imgUrl = null;
    this.cdr.detectChanges();
  }

  public resetImgInput(): void {
    // reset input
    if (this.uploadFileInput) {
      this.uploadFileInput.nativeElement.value = '';
    }
  }

  public uploadFile(filesList: FileList): void {
    if (!filesList || !filesList.length) { return; }

    const file = filesList[0];
    const allowedExtensions = ['jpeg', 'jpg', 'png', 'webp', 'svg'];
    const extension = file.name.split('.').pop().toLowerCase();

    if (!extension || !allowedExtensions.includes(extension)) {
      this.toasterService.notifyWarning(
        this.translateService.instant('COMMON.INVALID_FILE_TYPE', {file: file.name})
      );
      this.resetImgInput();
      return;
    }

    // check file size is less than 10MB
    const maxSize = this.maxFileSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      this.toasterService.notifyWarning(
        this.translateService.instant(
          'COMMON.OVERSIZE_FILE',
          {
            file: file.name,
            size: `${this.maxFileSizeMB} MB`
          }
        )
      );
      this.resetImgInput();
      return;
    }

    if (filesList && filesList.length) {
      // check if file is image
      if (!filesList[0].type.startsWith('image/')) {
        return;
      }
      this.images = filesList;
      this.imgUrl = URL.createObjectURL(filesList[0]);
    }

    getImageSize(this.imgUrl).then(size => {
      this.originalWidth = size.width;
      this.originalHeight = size.height;
      this.aspectRatio = size.width / size.height;
      this.setAspectRatioSizesOnImageUpload();
      this.updateFile.emit({ file: filesList[0], width: +this.widthControlValue, height: +this.heightControlValue });
      // // set defaults
      // this.widthControl.patchValue(Math.min(size.width, this.maxImageWidth));
      // this.heightControl.patchValue(Math.min(size.height, this.maxImageHeight));
    });
  }

  public setAspectRatioSizesOnImageUpload(): void {
    let calculatedSize: { width: number; height: number };

    if (this.originalWidth > this.originalHeight) {
      calculatedSize = this.resizeWithAspect(+this.widthControlValue, 'width');

    } else {
      calculatedSize = this.resizeWithAspect(+this.heightControlValue, 'height');
    }
    this.widthControl.patchValue(calculatedSize.width);
    this.heightControl.patchValue(calculatedSize.height);

    this.updateField(
      {
        width: calculatedSize.width,
        height: calculatedSize.height,
        useAspectRatio: this.useAspectRatioControl.value,
      },
      'dimensions'
    );
  }

  public decreaseQuantity(control: FormControl, fieldName: 'width'|'height'): void {
    const value = control.value;
    if (value > this.minImageSize) {
      control.patchValue(value - 1);
      this.updateSizeField(control.value, fieldName);
    }
  }

  public increaseQuantity(control: FormControl, fieldName: 'width'|'height'): void {
    const value = control.value || this.minImageSize;
    control.patchValue(value + 1);
    this.updateSizeField(control.value, fieldName);
  }

  // public setRelatedAspectSizes(): void {
  //   if (!this.useAspectRatioControl.value || !this.aspectRatio) {
  //     this.updateField(false, 'useAspectRatio');
  //     this.updateField(
  //       {
  //         width: this.widthControlValue,
  //         height: this.heightControlValue,
  //         useAspectRatio: this.useAspectRatioControl.value,
  //       },
  //       'dimensions'
  //     );
  //     return;
  //   }
  //
  //   const size = this.resizeWithAspect(
  //     this.widthControl.value || this.originalWidth,
  //     'width'
  //   );
  //
  //   this.widthControl.patchValue(size.width);
  //   this.heightControl.patchValue(size.height);
  //
  //   this.updateField(
  //     {
  //       width: size.width,
  //       height: size.height,
  //       useAspectRatio: this.useAspectRatioControl.value,
  //     },
  //     'dimensions'
  //   );
  // }

  private resizeWithAspect(baseValue: number, basedOn: 'width' | 'height'): { width: number; height: number } {
    let width: number;
    let height: number;

    if (this.imgRef && this.imgRef.nativeElement) {
      this.aspectRatio = this.imgRef.nativeElement.naturalWidth / this.imgRef.nativeElement.naturalHeight;
    }

    if (basedOn === 'width') {
      width = baseValue;
      height = Math.round(width / this.aspectRatio);
    } else {
      height = baseValue;
      width = Math.round(height * this.aspectRatio);
    }

    // enforce bounds while preserving ratio
    if (height > this.maxImageHeight) {
      height = this.maxImageHeight;
      width = Math.round(height * this.aspectRatio);
    }

    if (width > this.maxImageWidth) {
      width = this.maxImageWidth;
      height = Math.round(width / this.aspectRatio);
    }

    // enforce minimums
    if (height < this.minImageSize) {
      height = this.minImageSize;
      width = Math.round(height * this.aspectRatio);
    }

    if (width < this.minImageSize) {
      width = this.minImageSize;
      height = Math.round(width / this.aspectRatio);
    }

    return { width, height };
  }

  public setMediaType(type: PositionMediaTypeEnum): void {
    this.mediaType = type;
    this.cdr.detectChanges();
  }

  // get mediaTypeControlValue(): PositionMediaTypeEnum {
  //   return this.mediaTypeControl.value;
  // }

  public resetTextEditorValue(textContent: string): void {
    if (this.textEditorComponent) {
      this.textEditorComponent.resetTextContent(textContent);
    }
  }

  get alignmentControlValue(): string {
    return this.alignmentControl.value;
  }

  get useAspectRatioControlValue(): string {
    return this.useAspectRatioControl.value;
  }

  get heightControlValue(): string {
    return this.heightControl.value > this.maxImageHeight
      ? this.maxImageHeight
      : this.heightControl.value;
  }

  get widthControlValue(): string {
    return this.widthControl.value > this.maxImageWidth
      ? this.maxImageWidth
      : this.widthControl.value;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
